import React, { useEffect } from 'react';
import { Plus, X, Layers, ChevronDown, Percent } from 'lucide-react';
import { SkuItem } from '../types';
import { Button } from './ui/Button';
import { cn } from '../lib/utils';

export type OrderMode = 'single' | 'compare' | 'multiDesign';

interface SkuQuantityManagerProps {
  /** Current order mode */
  orderMode: OrderMode;
  /** Change order mode */
  onOrderModeChange: (mode: OrderMode) => void;
  /** Single quantity value (used in single mode) */
  quantity: number;
  /** Update single quantity */
  onQuantityChange: (quantity: number) => void;
  /** Array of SKUs (used in multiDesign mode) */
  skus: SkuItem[];
  /** Update SKUs array */
  onSkusChange: (skus: SkuItem[]) => void;
  /** Quantity tiers for compare mode */
  quantityTiers: number[];
  /** Update quantity tiers */
  onQuantityTiersChange: (tiers: number[]) => void;
  /** Selected tier index (for post-calculation dropdown) */
  selectedTierIndex: number;
  /** Change selected tier */
  onSelectedTierChange: (index: number) => void;
  /** Whether calculation has been done (switches to dropdown mode) */
  isCalculated: boolean;
  /** Savings percentages per tier (from API response) */
  tierSavings?: number[];
  /** Maximum number of SKUs allowed */
  maxSkus?: number;
  /** Maximum number of quantity tiers */
  maxTiers?: number;
  /** Whether form is loading */
  loading?: boolean;
}

const generateSkuId = () => `sku_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const SkuQuantityManager: React.FC<SkuQuantityManagerProps> = ({
  orderMode,
  onOrderModeChange,
  quantity,
  onQuantityChange,
  skus,
  onSkusChange,
  quantityTiers,
  onQuantityTiersChange,
  selectedTierIndex,
  onSelectedTierChange,
  isCalculated,
  tierSavings = [],
  maxSkus = 10,
  maxTiers = 5,
  loading = false
}) => {
  // Initialize with default SKU if empty in multiDesign mode
  useEffect(() => {
    if (orderMode === 'multiDesign' && skus.length === 0) {
      onSkusChange([
        { id: generateSkuId(), name: 'Design A', quantity: quantity || 1000 }
      ]);
    }
  }, [orderMode, skus.length, quantity, onSkusChange]);

  // Initialize with default tiers if empty in compare mode
  useEffect(() => {
    if (orderMode === 'compare' && quantityTiers.length === 0) {
      onQuantityTiersChange([quantity || 1000]);
    }
  }, [orderMode, quantityTiers.length, quantity, onQuantityTiersChange]);

  // Sync total quantity back to parent when in multiDesign mode
  useEffect(() => {
    if (orderMode === 'multiDesign' && skus.length > 0) {
      const total = skus.reduce((sum, sku) => sum + sku.quantity, 0);
      if (total !== quantity) {
        onQuantityChange(total);
      }
    }
  }, [orderMode, skus, quantity, onQuantityChange]);

  // SKU handlers (multiDesign mode)
  const handleAddSku = () => {
    if (skus.length >= maxSkus) return;
    const designLetter = String.fromCharCode(65 + skus.length);
    const newSku: SkuItem = {
      id: generateSkuId(),
      name: `Design ${designLetter}`,
      quantity: 1000
    };
    onSkusChange([...skus, newSku]);
  };

  const handleRemoveSku = (id: string) => {
    if (skus.length <= 1) return;
    onSkusChange(skus.filter(sku => sku.id !== id));
  };

  const handleSkuChange = (id: string, field: 'name' | 'quantity', value: string | number) => {
    onSkusChange(skus.map(sku => {
      if (sku.id !== id) return sku;
      return {
        ...sku,
        [field]: field === 'quantity' ? Math.max(1, Number(value) || 0) : value
      };
    }));
  };

  // Quantity tier handlers (compare mode)
  const handleAddTier = () => {
    if (quantityTiers.length >= maxTiers) return;
    const lastTier = quantityTiers[quantityTiers.length - 1] || 1000;
    onQuantityTiersChange([...quantityTiers, lastTier * 2]);
  };

  const handleRemoveTier = (index: number) => {
    if (quantityTiers.length <= 1) return;
    const newTiers = quantityTiers.filter((_, i) => i !== index);
    onQuantityTiersChange(newTiers);
    // Adjust selected tier index if needed
    if (selectedTierIndex >= newTiers.length) {
      onSelectedTierChange(newTiers.length - 1);
    }
  };

  const handleTierChange = (index: number, value: number) => {
    const newTiers = [...quantityTiers];
    newTiers[index] = Math.max(1, value);
    onQuantityTiersChange(newTiers);
  };

  const totalQuantity = orderMode === 'multiDesign'
    ? skus.reduce((sum, sku) => sum + sku.quantity, 0)
    : orderMode === 'compare'
      ? quantityTiers[selectedTierIndex] || quantityTiers[0] || 0
      : quantity;

  return (
    <div className="space-y-4">
      {/* Header with Mode Selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-1 h-1 bg-primary rounded-full"></div>
          <span className="text-sm font-medium text-foreground">Order Quantity</span>
        </div>
      </div>

      {/* Mode Selector - Radio Buttons (hidden after calculation) */}
      {!isCalculated && (
        <div className="flex gap-1 p-1 bg-muted/50 rounded-lg">
          <button
            type="button"
            onClick={() => onOrderModeChange('single')}
            disabled={loading}
            className={cn(
              "flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-all",
              orderMode === 'single'
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            Single
          </button>
          <button
            type="button"
            onClick={() => onOrderModeChange('compare')}
            disabled={loading}
            className={cn(
              "flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-all",
              orderMode === 'compare'
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            Compare Qty
          </button>
          <button
            type="button"
            onClick={() => onOrderModeChange('multiDesign')}
            disabled={loading}
            className={cn(
              "flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-all",
              orderMode === 'multiDesign'
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            Multi-Design
          </button>
        </div>
      )}

      {/* Single Quantity Mode */}
      {orderMode === 'single' && (
        <div className="space-y-2">
          <input
            type="number"
            value={quantity}
            onChange={(e) => onQuantityChange(Math.max(1, parseInt(e.target.value) || 0))}
            min={1}
            disabled={loading}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            placeholder="Enter quantity"
          />
          <p className="text-xs text-muted-foreground">
            Total boxes to order for this box size
          </p>
        </div>
      )}

      {/* Compare Quantities Mode */}
      {orderMode === 'compare' && (
        <div className="space-y-3">
          {!isCalculated ? (
            /* Input Mode - Before Calculation */
            <>
              <p className="text-xs text-muted-foreground">
                Add quantity tiers to compare pricing. Higher quantities often unlock better shipping rates.
              </p>

              {/* Quantity Tier List */}
              <div className="space-y-2">
                {quantityTiers.map((tier, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 p-3 bg-muted/30 border border-border rounded-lg"
                  >
                    <div className="flex-shrink-0 w-14 text-xs font-medium text-muted-foreground">
                      Qty {index + 1}:
                    </div>

                    <div className="flex-1 relative">
                      <input
                        type="number"
                        value={tier}
                        onChange={(e) => handleTierChange(index, parseInt(e.target.value) || 0)}
                        min={1}
                        disabled={loading}
                        className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 pr-8 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 text-right"
                        placeholder="Qty"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">pcs</span>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleRemoveTier(index)}
                      disabled={quantityTiers.length <= 1 || loading}
                      className={cn(
                        "p-1 rounded-md transition-colors",
                        quantityTiers.length <= 1 || loading
                          ? "text-muted-foreground/30 cursor-not-allowed"
                          : "text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      )}
                      title={quantityTiers.length <= 1 ? "At least one quantity is required" : "Remove quantity"}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Add Tier Button */}
              {quantityTiers.length < maxTiers && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddTier}
                  disabled={loading}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Quantity ({quantityTiers.length}/{maxTiers})
                </Button>
              )}
            </>
          ) : (
            /* Dropdown Mode - After Calculation */
            <>
              <p className="text-xs text-muted-foreground">
                Select a quantity tier to view its breakdown and configure shipping.
              </p>

              {/* Quantity Tier Dropdown */}
              <div className="relative">
                <select
                  value={selectedTierIndex}
                  onChange={(e) => onSelectedTierChange(parseInt(e.target.value))}
                  disabled={loading}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 appearance-none cursor-pointer"
                >
                  {quantityTiers.map((tier, index) => (
                    <option key={index} value={index}>
                      Qty {index + 1}: {tier.toLocaleString()} pcs
                      {tierSavings[index] > 0 ? ` (${tierSavings[index]}% savings)` : ''}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              </div>

              {/* Tier Summary Cards */}
              <div className="grid gap-2">
                {quantityTiers.map((tier, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => onSelectedTierChange(index)}
                    className={cn(
                      "flex items-center justify-between p-3 rounded-lg border transition-all text-left",
                      selectedTierIndex === index
                        ? "bg-primary/5 border-primary/30 ring-1 ring-primary/20"
                        : "bg-muted/30 border-border hover:border-primary/20"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <div className={cn(
                        "w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium",
                        selectedTierIndex === index
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground"
                      )}>
                        {index + 1}
                      </div>
                      <span className="text-sm font-medium">
                        {tier.toLocaleString()} pcs
                      </span>
                    </div>
                    {tierSavings[index] > 0 && (
                      <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                        <Percent className="h-3 w-3" />
                        <span>{tierSavings[index]}% savings</span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Multi-Design Mode */}
      {orderMode === 'multiDesign' && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Add multiple designs for the same box size. Each design can have a different quantity.
          </p>

          {/* SKU List */}
          <div className="space-y-2">
            {skus.map((sku, index) => (
              <div
                key={sku.id}
                className="flex items-center gap-2 p-3 bg-muted/30 border border-border rounded-lg"
              >
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-xs font-medium text-primary">{index + 1}</span>
                </div>

                <div className="flex-1 grid grid-cols-[1fr,120px] gap-2">
                  <input
                    type="text"
                    value={sku.name}
                    onChange={(e) => handleSkuChange(sku.id, 'name', e.target.value)}
                    disabled={loading}
                    className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="Design name"
                  />
                  <div className="relative">
                    <input
                      type="number"
                      value={sku.quantity}
                      onChange={(e) => handleSkuChange(sku.id, 'quantity', parseInt(e.target.value) || 0)}
                      min={1}
                      disabled={loading}
                      className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 pr-8 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 text-right"
                      placeholder="Qty"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">pcs</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleRemoveSku(sku.id)}
                  disabled={skus.length <= 1 || loading}
                  className={cn(
                    "p-1 rounded-md transition-colors",
                    skus.length <= 1 || loading
                      ? "text-muted-foreground/30 cursor-not-allowed"
                      : "text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                  )}
                  title={skus.length <= 1 ? "At least one design is required" : "Remove design"}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Add Design Button */}
          {skus.length < maxSkus && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddSku}
              disabled={loading}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Design ({skus.length}/{maxSkus})
            </Button>
          )}

          {/* Total Summary */}
          <div className="flex items-center justify-between p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <div className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-primary" />
              <span className="text-sm text-foreground">Total</span>
            </div>
            <div className="text-right">
              <span className="text-sm font-semibold text-foreground">
                {totalQuantity.toLocaleString()} boxes
              </span>
              <span className="text-xs text-muted-foreground ml-1">
                ({skus.length} design{skus.length !== 1 ? 's' : ''})
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkuQuantityManager;
